import {
    useCallback, useMemo,
} from 'react'

import useEnumValues from 'app/hooks/useEnumValues'
import useLabel from 'app/hooks/useLabel'
import SkyNetSpreadSheet, {
    getEditedItems,
    SkyNetSpreadSheetControlPanel,
} from 'app/shared-components/SkyNetSpreadSheet'
import {
    AsyncMutationOptions,
} from 'app/types/request.types'
import {
    FilterContextType,
    FilterOptions, FilterContext,
} from 'app/hooks/useFilter'
import {
    FeeChargeability,
} from 'app/enums/label.enums'

import useSyncState from 'app/hooks/useSyncState'
import requests from 'app/requests/RateCard/RateCard.requests'
import {
    AdditionalFeesTable,
} from '../AdditionalFees.types'

import feesFilterConfig from './AdditionalFeesSpreadsheet.filterConfig'
import getAdditionalFeesConfig from './AdditionalFeesSpreadsheet.config'

export default function AdditionalFeesSpreadsheet({
    filterOptions,
    onSave,
    onCancel,
}: Readonly<{
    filterOptions: FilterOptions,
    onSave?: (v: AdditionalFeesTable[], o?: AsyncMutationOptions) => void,
    onCancel?: (...args: any[]) => void,
}>) {
    const getAllowedValues = useEnumValues()
    const getLabel = useLabel()
    const {
        value,
        setValue,
    } = useSyncState<AdditionalFeesTable[]>(filterOptions?.filteredData as AdditionalFeesTable[])

    const editedItems = useMemo(() => {
        return getEditedItems({
            allItems: value,
            initData: filterOptions?.filteredData,
        })
    }, [
        filterOptions?.filteredData,
        value,
    ])

    const handleSave = useCallback(async (options?: { onSuccess?: (...a: any) => void }) => {
        return onSave(editedItems, options)
    }, [
        editedItems,
        onSave,
    ])

    const noAmountValue = useCallback((item: AdditionalFeesTable) => {
        return [
            FeeChargeability.PERCENTAGE_BASE_LEASE_POSITIONING_PERIOD,
            FeeChargeability.INCLUDED,
        ].includes(item.feeChargability)
    }, [])

    const config = useMemo(() => {
        return getAdditionalFeesConfig({
            getLabel,
            getAllowedValues,
            noAmountValue,
        })
    }, [
        getLabel,
        getAllowedValues,
        noAmountValue,
    ])

    const saveBeforeFilter = useCallback((setFilter) => {
        handleSave({
            onSuccess: setFilter,
        })
    }, [handleSave])

    const edited = Boolean(editedItems.length)

    const filterConfig: FilterContextType = useMemo(() => {
        return {
            ...filterOptions,
            config: feesFilterConfig,
            request: requests.regionalServiceAvailabilityFilterCount,
            beforeFilterCallback: saveBeforeFilter,
            edited,
        }
    }, [
        edited,
        filterOptions,
        saveBeforeFilter,
    ])

    return (
        <FilterContext.Provider value={filterConfig}>
            <SkyNetSpreadSheetControlPanel
                onSave={handleSave}
                onCancel={onCancel}
                edited={edited}
            >
                <SkyNetSpreadSheet
                    config={config}
                    value={value}
                    disabled={false}
                    onChange={setValue}
                />
            </SkyNetSpreadSheetControlPanel>
        </FilterContext.Provider>
    )
}
